import { Component } from '@theme/component';
import { getFocusableElements } from '@theme/focus';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /**
   * Tracks which element the click/keydown listeners are bound to so
   * closeDialog() can detach from the same target. Set in showDialog().
   *
   * @type {HTMLElement | null}
   */
  #listenerTarget = null;

  /**
   * Whether this dialog opened itself from the URL fragment rather than from a
   * click, which is what closeDialog() needs to know to put the visitor back on
   * the trigger and tidy the URL.
   *
   * @type {boolean}
   */
  #openedFromHash = false;

  /**
   * Elements this dialog made inert while open non-modally, so they can be
   * restored on close. See #inertBackground().
   *
   * @type {HTMLElement[]}
   */
  #inertedElements = [];

  /**
   * Handle for the frame queued in connectedCallback, so disconnectedCallback
   * can cancel it. Removing the hashchange listener does not stop an already
   * scheduled frame.
   *
   * @type {number | null}
   */
  #pendingHashFrame = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    if (this.hasAttribute('data-open-on-hash')) {
      window.addEventListener('hashchange', this.#openFromHash);

      // Deferred a frame so the browser has finished its own fragment jump and
      // laid the page out. Scrolling before that lands on a stale offset.
      this.#pendingHashFrame = requestAnimationFrame(this.#openFromHash);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    window.removeEventListener('hashchange', this.#openFromHash);

    // Never leave the page inert if the dialog goes away while open.
    this.#restoreBackground();

    if (this.#pendingHashFrame !== null) {
      cancelAnimationFrame(this.#pendingHashFrame);
      this.#pendingHashFrame = null;
    }
  }

  /**
   * The current fragment, decoded. Decoding lets an id survive being pasted
   * back out of a URL bar; a malformed escape throws rather than returning the
   * raw string, so it falls back to the undecoded value.
   *
   * @returns {string} The fragment without its leading `#`.
   */
  #hashTarget() {
    const raw = window.location.hash.slice(1);

    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }

  /**
   * Opens the dialog when the fragment names this component, so a link like
   * `/products/everyday-energy#nutrition-facts` lands on the trigger with the
   * dialog already open. Bound as a field so it can be added and removed as a
   * listener, and so `this` survives being called from one.
   */
  #openFromHash = () => {
    this.#pendingHashFrame = null;

    // showModal() on a detached dialog throws InvalidStateError, and a section
    // re-render can disconnect this component between the frame being queued
    // and it running. Cancelling in disconnectedCallback covers that; this
    // guards the case where the element is out of the document for any other
    // reason by the time the callback fires.
    if (!this.isConnected || !this.id) return;

    const { dialog } = this.refs;

    if (dialog.open || this.#hashTarget() !== this.id) return;

    // Two popups given the same Direct link ID are a misconfiguration, but they
    // should not open on top of each other. getElementById resolves to the first
    // match, which is also where the browser's own fragment jump goes, so
    // deferring to it keeps the JS and the no-JS behaviour pointing at the same
    // popup.
    if (document.getElementById(this.id) !== this) return;

    // Deliberately not scrolling here. A modal covers the page, so the scroll
    // position while it is open is invisible; and on an image-heavy page the
    // lazy images have not loaded yet, so anything measured now is stale by the
    // time it matters. #revealTrigger() does it on close instead.
    this.#openedFromHash = true;
    this.showDialog();
  };

  /**
   * Puts the visitor on the trigger after a dialog that opened itself from the
   * fragment closes. Without this they are left wherever the page happened to
   * be, with no idea what they just dismissed.
   */
  #revealTrigger() {
    // `nearest`, not `center`: arriving on a fragment, the browser has already
    // scrolled the trigger to the top of the viewport, and re-centring it on
    // close threw the page 400px upwards for no reason the visitor could see.
    // This moves the minimum needed, and nothing at all when it is already in
    // view — which is the common case, and the point. `auto` defers to the
    // theme's `scroll-behavior: smooth`, so a large correction animates rather
    // than jumping.
    this.scrollIntoView({ block: 'nearest', behavior: 'auto' });

    // A dialog opened without a click has no previously focused element for the
    // browser to restore to, so focus would drop to the top of the document.
    // Another auto-opening dialog can also have taken focus in the meantime.
    this.querySelector('button')?.focus({ preventScroll: true });
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   *
   * Two opening modes are supported:
   *
   *   1. Modal (default) — dialog.showModal(). The dialog is promoted into
   *      the top layer, the rest of the page is marked inert, the browser
   *      draws a ::backdrop pseudo-element, and aria-modal="true" is
   *      exposed implicitly. Used by the cart drawer and most popovers.
   *
   *   2. Non-modal — dialog.show(). The dialog stays in normal DOM flow,
   *      so z-index works as usual and the page chrome behind it (e.g. a
   *      header with a transparent / image background) can still render
   *      above it. Opt in by setting `data-non-modal` on the
   *      <dialog-component>; the template is then responsible for
   *      providing its own dim layer (typically a sibling
   *      .menu-drawer__backdrop div) since the browser doesn't draw
   *      ::backdrop in this mode. The search modal uses this so the
   *      sticky header stays visible when search opens.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const nonModal = this.hasAttribute('data-non-modal');

    if (nonModal) {
      dialog.show();
      this.#inertBackground();
    } else {
      dialog.showModal();
    }

    // Move focus to the dialog itself rather than letting showModal() pick
    // the first focusable descendant (which would be the close button).
    // Focusing the dialog means the screen reader announces the dialog's
    // label — e.g. "Shopping Cart, dialog" — giving the user immediate
    // context. They then navigate inward (VO+Down to enter the group, VO+Right
    // or Tab to walk through interactive elements) at their own pace, instead
    // of being asked to act on a destructive "Close" button before they know
    // what opened. Requires tabindex="-1" on the <dialog> in the template;
    // without it, .focus() is a no-op for a non-interactive element.
    if (dialog.hasAttribute('tabindex')) {
      dialog.focus();
    }

    // Belt-and-suspenders scroll lock. Modal dialogs already inert the page
    // but iOS Safari can still scroll behind them; non-modal dialogs don't
    // inert anything so the lock is the only thing stopping background
    // scroll. Removed in closeDialog().
    document.documentElement.setAttribute('scroll-lock', '');

    this.dispatchEvent(new DialogOpenEvent());

    if (nonModal) {
      // Non-modal: the custom backdrop is a sibling of the <dialog> inside
      // this component, so we listen on the component to catch clicks on
      // either the backdrop or anywhere outside the dialog. Defer attachment
      // by one frame so the click that opened the dialog finishes
      // propagating without immediately re-closing it.
      this.#listenerTarget = this;
      requestAnimationFrame(() => {
        // Guard against rapid open→close races where closeDialog() ran
        // before this rAF fires.
        if (!dialog.open) return;
        this.addEventListener('click', this.#handleClick);
        this.addEventListener('keydown', this.#handleKeyDown);
      });
    } else {
      // Modal: attach to the <dialog> itself, not the component.
      // The trigger button (e.g. the cart icon) is a sibling of the <dialog>,
      // not a descendant — so the click that opened the dialog cannot bubble
      // through here and accidentally close it. Backdrop clicks with showModal()
      // target the <dialog> element itself, so isClickedOutside still works.
      this.#listenerTarget = dialog;
      dialog.addEventListener('click', this.#handleClick);
      dialog.addEventListener('keydown', this.#handleKeyDown);
    }

    document.body?.classList.add('dialog-open');
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Detach from whichever target showDialog() bound to (the dialog for
    // modal mode, the component for non-modal mode).
    this.#listenerTarget?.removeEventListener('click', this.#handleClick);
    this.#listenerTarget?.removeEventListener('keydown', this.#handleKeyDown);
    this.#listenerTarget = null;

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.documentElement.removeAttribute('scroll-lock');

    this.#restoreBackground();
    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.#openedFromHash) {
      this.#openedFromHash = false;
      this.#revealTrigger();

      // Drop the fragment that opened this dialog. Without it the URL still
      // names something the visitor has just dismissed, so reloading or sharing
      // reopens it, and following the same in-page link again fires no
      // hashchange and does nothing. replaceState keeps it out of the back
      // stack.
      if (this.#hashTarget() === this.id) {
        // `history.state` is passed back rather than nulled: other code on the
        // page owns that state, and this only means to drop the fragment. URL
        // does the stripping so nothing else in the address is disturbed.
        const url = new URL(window.location.href);
        url.hash = '';
        history.replaceState(history.state, '', url);
      }
    }

    this.dispatchEvent(new DialogCloseEvent());
    const body = document.querySelector('body');
    body?.classList.remove('dialog-open');
    body?.classList.add('dialog-closed');
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   * Arrow function field so `this` is the component even when the listener
   * is attached to the <dialog> element.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key, and keeps Tab
   * inside the dialog when it was opened non-modally.
   * Arrow function field so `this` is the component even when the listener
   * is attached to the <dialog> element.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Tab') {
      this.#wrapTabFocus(event);
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Makes the page behind a non-modal dialog unreachable while it is open.
   *
   * showModal() inerts the whole document for us; show() inerts nothing, so a
   * screen reader's reading cursor could still walk out of the open dialog into
   * the page it covers (Tab is kept in by #wrapTabFocus, but the reading cursor
   * doesn't move focus). The template names what sits behind the dialog in
   * data-inert-while-open, a selector -- for the search modal, the main content
   * and footer. The sticky header, which stays visible above the search modal,
   * is deliberately not in it.
   */
  #inertBackground() {
    const selector = this.dataset.inertWhileOpen;
    if (!selector) return;

    for (const element of document.querySelectorAll(selector)) {
      // Skip anything already inert (not ours to undo) and any ancestor of this
      // dialog, which would inert the dialog itself.
      if (!(element instanceof HTMLElement) || element.inert || element.contains(this)) continue;

      element.inert = true;
      this.#inertedElements.push(element);
    }

    // Safety net: however the dialog ends up closed, never leave the page inert.
    // closeDialog() restores first; this covers any path that bypasses it. The
    // close event fires after a task, so skip it if the dialog has been reopened
    // in between; that reopening owns the inert state now.
    const { dialog } = this.refs;
    dialog.addEventListener(
      'close',
      () => {
        if (!dialog.open) this.#restoreBackground();
      },
      { once: true }
    );
  }

  /**
   * Undoes #inertBackground(), touching only the elements it inerted.
   */
  #restoreBackground() {
    for (const element of this.#inertedElements) {
      element.inert = false;
    }
    this.#inertedElements = [];
  }

  /**
   * Wraps focus back to the other end of a non-modal dialog when Tab or
   * Shift+Tab reaches its edge, keeping keyboard focus inside.
   *
   * showModal() marks the rest of the document inert, so the browser keeps
   * focus in modal dialogs for us. show() inerts nothing — without this, Tab
   * walks straight out of the open dialog into the page behind it, which is
   * covered and effectively invisible. Only the non-modal path (currently
   * just the search modal) needs handling.
   *
   * Deliberately NOT @theme/focus's trapFocus(), and named differently to
   * keep the two apart. That one installs a persistent document-level trap
   * from a snapshot of the focusable elements taken when it is called; this
   * one handles a single keypress against a live query. The distinction
   * matters here because predictive search morphs its results in as the user
   * types, so a snapshot taken at open time would point at detached nodes
   * after the first keystroke.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #wrapTabFocus = (event) => {
    if (!this.hasAttribute('data-non-modal')) return;

    // `button:enabled` in the shared selector still matches `hidden` buttons
    // (hidden is not disabled) — e.g. the search modal's reset button before
    // any query is typed. Focusing one would silently break the cycle, so
    // drop anything that isn't actually rendered. getClientRects() is used
    // over offsetParent because it stays correct for position: fixed.
    const { dialog } = this.refs;
    const focusable = getFocusableElements(dialog).filter((element) => element.getClientRects().length > 0);

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) return;

    // The dialog element itself counts as the backward edge. showDialog()
    // focuses it whenever the template supplies tabindex="-1" (as the cart
    // drawer does), and Shift+Tab from there would otherwise fall out the back
    // of a non-modal dialog. Only the search modal is non-modal today and it
    // has no tabindex, so this is guarding against a template change rather
    // than a live bug. Forward needs no equivalent: Tab from the dialog lands
    // on its first focusable descendant on its own.
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
